<template>
  <div class="layout">
    <!-- 顶部黑色工具条 -->
    <div class="topbar">
      <div class="container topbar-inner">
        <span class="topbar-left">欢迎来到铠盾精选，企业集采免登录直采</span>
        <div class="topbar-right">
          <template v-if="isLogin">
            <span class="topbar-link alias">您好，{{ alias || "经销商" }}</span>
            <span class="divider"></span>
            <span class="topbar-link" @click="doLogout">退出登录</span>
          </template>
          <template v-else>
            <span class="topbar-link" @click="goLogin">经销商登录</span>
          </template>
          <span class="divider"></span>
          <span class="topbar-link" @click="$router.push({ name: 'enterpriseOrderQuery' })">
            集采订单查询
          </span>
          <template v-if="isLogin">
            <span class="divider"></span>
            <span
              class="topbar-link"
              :class="{ 'topbar-active': $route.name === 'selectionRecords' }"
              @click="$router.push({ name: 'selectionRecords' })"
            >
              商城记录
            </span>
          </template>
        </div>
      </div>
    </div>

    <!-- 主导航 -->
    <div class="header">
      <div class="container header-inner">
        <div class="logo" @click="$router.push({ name: 'home' })">
          <img class="logo-img" :src="logoImg" alt="铠盾精选" />
        </div>
        <div class="nav">
          <div
            class="nav-item"
            :class="{ active: $route.name === 'home' }"
            @click="$router.push({ name: 'home' })"
          >
            首页
          </div>
          <div
            class="nav-item"
            :class="{ active: isActive('enterprise') }"
            @click="$router.push({ name: 'enterpriseList' })"
          >
            企业集采
          </div>
          <div
            class="nav-item"
            :class="{ active: isActive('selection') }"
            @click="goSelection"
          >
            经销商选品
          </div>
        </div>
        <div class="header-right">
          <div class="search-box">
            <input
              class="search-input"
              v-model="searchKeyword"
              placeholder="搜索商品，回车搜索"
              @keyup.enter="doSearch"
            />
            <button class="search-btn" @click="doSearch">
              <i class="el-icon-search"></i>
            </button>
          </div>
          <div class="cart-btn" @click="$router.push({ name: 'enterpriseCart' })">
            <i class="el-icon-shopping-cart-2"></i>
            <span>采购车</span>
            <span class="cart-count" v-if="cartCount > 0">{{ cartCount }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 内容 -->
    <div class="page-main">
      <div class="container">
        <router-view />
      </div>
    </div>

    <!-- 页脚 -->
    <div class="footer">
      <div class="container footer-inner">
        <div class="footer-links">
          <span>企业集采 · 免登录直采</span>
          <span class="footer-divider"></span>
          <span>经销商选品平台</span>
        </div>
        <p class="footer-copy">
          <span>Copyright © 2024-{{new Date().getFullYear()}} 福建铠盾材料科技有限公司 All Rights Reserved</span> | <el-link href="https://beian.miit.gov.cn/" target="_blank" style="font-size:12px;margin-top: -1px;color: #b0b0b0;">闽ICP备2024042932号</el-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { cartCount } from "@/utils/purchaseStore";
import { logout } from "@/api";
import logoImg from "@/assets/logo.jpg";

export default {
  name: "Layout",
  data() {
    return {
      logoImg,
      cartCount: 0,
      searchKeyword: "",
    };
  },
  computed: {
    ...mapGetters(["isLogin"]),
    ...mapState(["alias"]),
  },
  watch: {
    $route: {
      handler() {
        this.cartCount = cartCount();
        if (this.isLogin && !this.alias) {
          this.$store.dispatch("fetchLoginInfo").catch(() => {});
        }
      },
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener("purchase-cart-changed", this.onCartChanged);
    this.cartCount = cartCount();
    if (this.isLogin && !this.alias) {
      this.$store.dispatch("fetchLoginInfo").catch(() => {});
    }
  },
  beforeDestroy() {
    window.removeEventListener("purchase-cart-changed", this.onCartChanged);
  },
  methods: {
    onCartChanged() {
      this.cartCount = cartCount();
    },
    doSearch() {
      const kw = (this.searchKeyword || "").trim();
      if (!kw) {
        this.$message.info("请输入要搜索的商品名称");
        return;
      }
      this.$router.push({
        name: "enterpriseList",
        query: { keyword: kw },
      });
    },
    isActive(prefix) {
      const name = this.$route.name || "";
      return name.startsWith(prefix);
    },
    goSelection() {
      if (!this.isLogin) {
        this.$router.push({
          name: "login",
          query: { redirect: this.$route.fullPath },
        });
        return;
      }
      this.$router.push({ name: "selectionList" });
    },
    goLogin() {
      this.$router.push({
        name: "login",
        query: { redirect: this.$route.fullPath },
      });
    },
    doLogout() {
      logout().catch(() => {});
      this.$store.commit("clearAuth");
      this.$message.success("已退出登录");
      if ((this.$route.name || "").startsWith("selection")) {
        this.$router.push({ name: "enterpriseList" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #ff6700;
$primary-dark: #e65c00;

.layout {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

/* ---------- 顶部工具条 ---------- */
.topbar {
  height: 40px;
  font-size: 12px;
  line-height: 40px;
  color: #b0b0b0;
  background: #333;

  .topbar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .topbar-right {
    display: flex;
    align-items: center;
  }

  .topbar-link {
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #fff;
    }

    &.alias {
      color: #fff;
      cursor: default;
    }

    &.topbar-active {
      color: $primary;
    }
  }

  .divider {
    width: 1px;
    height: 12px;
    margin: 0 12px;
    background: #424242;
  }
}

/* ---------- 主导航 ---------- */
.header {
  height: 100px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .header-inner {
    display: flex;
    align-items: center;
    height: 100%;
  }
}

.logo {
  display: flex;
  align-items: center;
  margin-right: 20px;
  margin-top: 10px;
  cursor: pointer;

  .logo-img {
    display: block;
    height: 32px;
    object-fit: contain;
  }
}

.nav {
  display: flex;
  align-items: center;
  flex: 1;

  .nav-item {
    position: relative;
    padding: 0 20px;
    font-size: 16px;
    line-height: 100px;
    color: #333;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: $primary;
    }

    &.active {
      color: $primary;

      &::after {
        position: absolute;
        bottom: 16px;
        left: 20px;
        right: 20px;
        height: 3px;
        background: $primary;
        content: "";
      }
    }
  }
}

.header-right {
  display: flex;
  align-items: center;

  .search-box {
    display: flex;
    align-items: center;
    width: 260px;
    height: 42px;
    margin-right: 20px;
    border: 1px solid #e0e0e0;
    transition: border-color 0.2s;

    &:focus-within {
      border-color: $primary;
    }

    .search-input {
      flex: 1;
      min-width: 0;
      padding: 0 12px;
      font-size: 13px;
      color: #333;
      background: transparent;
      border: none;
      outline: none;

      &::placeholder {
        color: #b0b0b0;
      }
    }

    .search-btn {
      width: 48px;
      height: 100%;
      font-size: 18px;
      color: #757575;
      background: #fff;
      border: none;
      border-left: 1px solid #e0e0e0;
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: $primary;
      }
    }
  }

  .cart-btn {
    position: relative;
    width: 120px;
    height: 42px;
    font-size: 15px;
    line-height: 42px;
    color: #fff;
    text-align: center;
    cursor: pointer;
    background: #333;
    transition: background 0.2s;

    i {
      margin-right: 6px;
      font-size: 17px;
      color: $primary;
    }

    &:hover {
      background: $primary;

      i {
        color: #fff;
      }
    }

    .cart-count {
      position: absolute;
      top: -8px;
      right: -8px;
      min-width: 18px;
      height: 18px;
      font-size: 12px;
      line-height: 18px;
      color: #fff;
      background: #83c44e;
      border-radius: 9px;
    }
  }
}

/* ---------- 页脚 ---------- */
.footer {
  padding: 30px 0;
  background: #fff;

  .footer-inner {
    text-align: center;
  }

  .footer-links {
    font-size: 14px;
    color: #757575;

    .footer-divider {
      display: inline-block;
      width: 1px;
      height: 12px;
      margin: 0 16px;
      background: #e0e0e0;
    }
  }

  .footer-copy {
    margin-top: 10px;
    font-size: 12px;
    color: #b0b0b0;
  }
}
</style>
